import { useState, useMemo, useRef, useEffect } from "react";
import { Calculator, Scale, Droplets, RefreshCw, ChevronDown, Info, Copy, Share2, Check, Lock, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage } from "../i18n/LanguageContext";
import { SEO } from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Base formula for 100g batch (updated formula - reduced baking soda)
const BASE_FORMULA = {
  bakingSoda: { percent: 15, density: 2.2, minPercent: 12, maxPercent: 18 },
  cornStarch: { percent: 17.5, density: 0.56 },
  coconutOil: { percent: 37.5, density: 0.92 },
  sheaButter: { percent: 20, density: 0.9 },
  beeswax: { percent: 5, density: 0.96 },
  essentialOil: { percent: 0.5, density: 0.9 },
  aloeVera: { percent: 4.5, density: 1.0 }
};

// Spoon conversions
const SPOON_CONVERSIONS = {
  teaspoon: 5,
  tablespoon: 15,
  drop: 0.05
};

export const QuantityCalculator = () => {
  const { language } = useLanguage();
  const isRTL = language === 'he';
  const resultsRef = useRef(null);
  
  // User authentication state
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const [mode, setMode] = useState('preset');
  const [presetAmount, setPresetAmount] = useState(100);
  const [customIngredient, setCustomIngredient] = useState('bakingSoda');
  const [customAmount, setCustomAmount] = useState('');
  const [showInfo, setShowInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);

  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Get token from localStorage (for cross-domain support)
        const storedToken = localStorage.getItem('mart_session_token');
        
        const headers = {};
        if (storedToken) {
          headers['Authorization'] = `Bearer ${storedToken}`;
        }
        
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include',
          headers,
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          // Clear invalid token
          localStorage.removeItem('mart_session_token');
        }
      } catch (error) {
        console.error("Auth check failed:", error);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  const handleLogin = () => {
    // Save current page to return to after login
    localStorage.setItem('mart_auth_redirect', '/calculator');
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const ingredients = useMemo(() => ({
    bakingSoda: { 
      he: 'סודיום ביקרבונט', 
      en: 'Sodium Bicarbonate', 
      icon: '🧂',
      note: {
        he: 'סודה לשתייה באיכות קוסמטית',
        en: 'Cosmetic-grade baking soda'
      }
    },
    cornStarch: { he: 'עמילן תירס', en: 'Corn Starch', icon: '🌽' },
    coconutOil: { he: 'שמן קוקוס', en: 'Coconut Oil', icon: '🥥' },
    sheaButter: { he: 'חמאת שיאה', en: 'Shea Butter', icon: '🧈' },
    beeswax: { he: 'שעוות דבורים', en: 'Beeswax', icon: '🐝' },
    essentialOil: { he: 'שמנים אתריים', en: 'Essential Oils', icon: '💧' },
    aloeVera: { he: "ג'ל אלוורה", en: 'Aloe Vera Gel', icon: '🌱' }
  }), []);

  const calculatedAmounts = useMemo(() => {
    let totalGrams = presetAmount;
    
    if (mode === 'custom' && customAmount) {
      const customGrams = parseFloat(customAmount);
      const customPercent = BASE_FORMULA[customIngredient].percent;
      totalGrams = (customGrams / customPercent) * 100;
    }
    
    const results = {};
    Object.keys(BASE_FORMULA).forEach(key => {
      const grams = (BASE_FORMULA[key].percent / 100) * totalGrams;
      const density = BASE_FORMULA[key].density;
      const ml = grams / density;
      
      let teaspoons = grams / SPOON_CONVERSIONS.teaspoon;
      let tablespoons = grams / SPOON_CONVERSIONS.tablespoon;
      let drops = key === 'essentialOil' ? grams / SPOON_CONVERSIONS.drop : null;
      
      results[key] = {
        grams: Math.round(grams * 10) / 10,
        ml: Math.round(ml * 10) / 10,
        teaspoons: Math.round(teaspoons * 10) / 10,
        tablespoons: Math.round(tablespoons * 10) / 10,
        drops: drops ? Math.round(drops) : null
      };
    });
    
    return { amounts: results, totalGrams: Math.round(totalGrams) };
  }, [mode, presetAmount, customIngredient, customAmount]);

  const generateTextSummary = () => {
    const title = isRTL ? `📋 רשימת חומרים לדאודורנט טבעי (${calculatedAmounts.totalGrams} גרם)` : `📋 Natural Deodorant Ingredients (${calculatedAmounts.totalGrams}g)`;
    const divider = '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━';
    
    let text = `${title}\n${divider}\n\n`;
    
    Object.keys(BASE_FORMULA).forEach(key => {
      const ing = ingredients[key];
      const amounts = calculatedAmounts.amounts[key];
      const name = isRTL ? ing.he : ing.en;
      const isEssential = key === 'essentialOil';
      
      if (isRTL) {
        text += `${ing.icon} ${name}\n`;
        text += `   • ${amounts.grams} גרם | ${amounts.ml} מ"ל\n`;
        if (isEssential) {
          text += `   • ${amounts.drops} טיפות\n`;
        } else {
          text += `   • ${amounts.teaspoons} כפיות | ${amounts.tablespoons} כפות\n`;
        }
        text += '\n';
      } else {
        text += `${ing.icon} ${name}\n`;
        text += `   • ${amounts.grams}g | ${amounts.ml}ml\n`;
        if (isEssential) {
          text += `   • ${amounts.drops} drops\n`;
        } else {
          text += `   • ${amounts.teaspoons} tsp | ${amounts.tablespoons} tbsp\n`;
        }
        text += '\n';
      }
    });
    
    text += divider + '\n';
    text += isRTL ? '🌿 M\'art - דאודורנט טבעי' : '🌿 M\'art - Natural Deodorant';
    
    return text;
  };

  const handleCopy = async () => {
    const text = generateTextSummary();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = async () => {
    const text = generateTextSummary();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: isRTL ? 'רשימת חומרים לדאודורנט' : 'Deodorant Ingredients List',
          text: text
        });
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopy();
        }
      }
    } else {
      handleCopy();
    }
  };

  const resetCalculator = () => {
    setMode('preset');
    setPresetAmount(100);
    setCustomAmount('');
    setCustomIngredient('bakingSoda');
  };

  // Login Gate Component
  const LoginGate = () => (
    <div className="min-h-screen pt-20 pb-12">
      <SEO page="calculator" />
      <section className="px-4 sm:px-6">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-[#4A5D43] to-[#3A4A35] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Calculator className="h-10 w-10 text-white" />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#2C3329] mb-4">
              {isRTL ? 'מחשבון חומרים לייצור' : 'Materials Calculator'}
            </h1>
            <p className="text-[#6B7266] text-lg">
              {isRTL 
                ? 'כלי מקצועי לחישוב כמויות מדויקות לדאודורנט טבעי'
                : 'Professional tool for calculating exact natural deodorant quantities'
              }
            </p>
          </div>
          
          {/* Login Required Card */}
          <div className="premium-card rounded-2xl p-6 sm:p-8 mb-6 border-2 border-[#E8E4DC]">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="h-6 w-6 text-[#4A5D43]" />
              <h2 className="font-serif text-xl text-[#2C3329]">
                {isRTL ? 'נדרשת התחברות' : 'Login Required'}
              </h2>
            </div>
            
            <p className="text-[#6B7266] mb-6">
              {isRTL 
                ? 'כדי להשתמש במחשבון החומרים, יש להתחבר עם חשבון Google. ההתחברות מאפשרת לנו לשמור את החישובים שלך בצורה מאובטחת ולהציע חוויה מותאמת אישית.'
                : 'To use the materials calculator, please log in with your Google account. This allows us to securely save your calculations and provide a personalized experience.'
              }
            </p>
            
            {/* Security Note */}
            <div className="bg-[#F5F2EB] rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-[#4A5D43] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-[#2C3329] text-sm mb-1">
                    {isRTL ? 'מידע מאובטח' : 'Secure Information'}
                  </h4>
                  <p className="text-xs text-[#6B7266]">
                    {isRTL 
                      ? 'המידע שלך נשמר בצורה מאובטחת ולא משותף עם צדדים שלישיים'
                      : 'Your information is stored securely and not shared with third parties'
                    }
                  </p>
                </div>
              </div>
            </div>
            
            {/* Login Button */}
            <Button 
              onClick={handleLogin}
              className="w-full bg-white text-[#2C3329] border-2 border-[#E8E4DC] hover:bg-[#F5F2EB] rounded-xl py-6 shadow-md hover:shadow-lg transition-all"
            >
              <svg className={`${isRTL ? 'ml-3' : 'mr-3'} h-5 w-5`} viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {isRTL ? 'התחברות עם Google' : 'Sign in with Google'}
            </Button>
          </div>
          
          {/* Preview of what they'll get */}
          <div className="premium-card rounded-2xl p-6 bg-[#F5F2EB]/50">
            <h3 className="font-medium text-[#2C3329] mb-3 flex items-center gap-2">
              <Calculator className="h-5 w-5 text-[#4A5D43]" />
              {isRTL ? 'מה תקבלו?' : 'What you\'ll get:'}
            </h3>
            <ul className="space-y-2 text-sm text-[#6B7266]">
              {(isRTL ? [
                'חישוב כמויות מדויק בגרמים, כפיות וכפות',
                'בחירת כמות סופית או חישוב לפי חומר קיים',
                'שמירת החישובים שלך',
                'שיתוף רשימת חומרים'
              ] : [
                'Exact quantity calculation in grams, teaspoons and tablespoons',
                'Choose final amount or calculate by existing ingredient',
                'Save your calculations',
                'Share ingredients list'
              ]).map((item, idx) => (
                <li key={idx} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-[#4A5D43]" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#4A5D43] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // If not logged in, show login gate
  if (!user) {
    return <LoginGate />;
  }

  // Main calculator (shown after login)
  return (
    <div className="min-h-screen pt-18 pb-10">
      <SEO page="calculator" />
      
      {/* User Badge */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 mb-3">
        <div className="flex items-center gap-2 text-sm text-[#6B7266]">
          <User className="h-4 w-4" />
          <span>{isRTL ? 'מחובר כ:' : 'Logged in as:'}</span>
          <span className="font-medium text-[#2C3329]">{user.name || user.email}</span>
        </div>
      </div>
      
      {/* Hero Header */}
      <section className="px-4 sm:px-6 mb-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-14 h-14 bg-gradient-to-br from-[#4A5D43] to-[#3A4A35] rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
            <Calculator className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-[#2C3329] mb-2">
            {isRTL ? 'מחשבון חומרים לייצור' : 'Materials Calculator'}
          </h1>
          <p className="text-base sm:text-lg text-[#6B7266] max-w-2xl mx-auto">
            {isRTL ? 'חשבו את הכמויות המדויקות לפי כפיות, כפות או גרמים' : 'Calculate exact quantities in teaspoons, tablespoons or grams'}
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Mode Selection */}
        <div className="premium-card rounded-2xl p-4 mb-3">
          <h3 className="font-serif text-lg text-[#2C3329] mb-4">
            {isRTL ? 'בחרו שיטת חישוב' : 'Choose Calculation Method'}
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => setMode('preset')}
              className={`p-5 rounded-xl border-2 transition-all text-${isRTL ? 'right' : 'left'} ${
                mode === 'preset' ? 'border-[#4A5D43] bg-[#4A5D43]/5' : 'border-[#E8E4DC] hover:border-[#4A5D43]/50'
              }`}
            >
              <Scale className={`h-6 w-6 mb-2 ${mode === 'preset' ? 'text-[#4A5D43]' : 'text-[#6B7266]'}`} />
              <p className="font-medium text-[#2C3329]">{isRTL ? 'לפי כמות סופית' : 'By Final Amount'}</p>
              <p className="text-sm text-[#6B7266]">{isRTL ? 'בחרו 30 גרם או 100 גרם' : 'Choose 30g or 100g batch'}</p>
            </button>

            <button
              onClick={() => setMode('custom')}
              className={`p-5 rounded-xl border-2 transition-all text-${isRTL ? 'right' : 'left'} ${
                mode === 'custom' ? 'border-[#4A5D43] bg-[#4A5D43]/5' : 'border-[#E8E4DC] hover:border-[#4A5D43]/50'
              }`}
            >
              <Droplets className={`h-6 w-6 mb-2 ${mode === 'custom' ? 'text-[#4A5D43]' : 'text-[#6B7266]'}`} />
              <p className="font-medium text-[#2C3329]">{isRTL ? 'לפי מה שיש לי' : 'By What I Have'}</p>
              <p className="text-sm text-[#6B7266]">{isRTL ? 'הכניסו כמה יש לכם מרכיב אחד' : 'Enter how much of one ingredient you have'}</p>
            </button>
          </div>
        </div>

        {/* Input Section */}
        <div className="premium-card rounded-2xl p-4 mb-3">
          {mode === 'preset' ? (
            <div>
              <h3 className="font-serif text-lg text-[#2C3329] mb-4">{isRTL ? 'בחרו כמות סופית' : 'Select Final Amount'}</h3>
              <div className="flex gap-3">
                <button
                  onClick={() => setPresetAmount(30)}
                  className={`flex-1 py-4 rounded-xl font-medium text-lg transition-all ${
                    presetAmount === 30 ? 'bg-[#4A5D43] text-white shadow-lg' : 'bg-[#F5F2EB] text-[#2C3329] hover:bg-[#E8E4DC]'
                  }`}
                >
                  30 {isRTL ? 'גרם' : 'grams'}
                </button>
                <button
                  onClick={() => setPresetAmount(100)}
                  className={`flex-1 py-4 rounded-xl font-medium text-lg transition-all ${
                    presetAmount === 100 ? 'bg-[#4A5D43] text-white shadow-lg' : 'bg-[#F5F2EB] text-[#2C3329] hover:bg-[#E8E4DC]'
                  }`}
                >
                  100 {isRTL ? 'גרם' : 'grams'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <h3 className="font-serif text-lg text-[#2C3329] mb-4">{isRTL ? 'מה יש לכם?' : 'What do you have?'}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#6B7266] mb-2">{isRTL ? 'בחרו מרכיב' : 'Select Ingredient'}</label>
                  <select
                    value={customIngredient}
                    onChange={(e) => setCustomIngredient(e.target.value)}
                    className="w-full p-4 bg-[#F5F2EB] border border-[#E8E4DC] rounded-xl text-[#2C3329] focus:outline-none focus:border-[#4A5D43]"
                  >
                    {Object.keys(ingredients).map(key => (
                      <option key={key} value={key}>
                        {ingredients[key].icon} {isRTL ? ingredients[key].he : ingredients[key].en}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[#6B7266] mb-2">{isRTL ? 'כמה גרם יש לכם?' : 'How many grams?'}</label>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder={isRTL ? 'הכניסו כמות' : 'Enter amount'}
                    className="w-full p-4 bg-[#F5F2EB] border border-[#E8E4DC] rounded-xl text-[#2C3329] focus:outline-none focus:border-[#4A5D43]"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Section - Mobile Friendly */}
        <div ref={resultsRef} className="premium-card rounded-2xl overflow-hidden mb-3">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#4A5D43] to-[#3A4A35] text-white p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-serif text-lg sm:text-xl mb-1">
                  {isRTL ? '📋 רשימת חומרים' : '📋 Ingredients List'}
                </h3>
                <p className="text-white/80 text-sm">
                  {isRTL ? `סה״כ: ${calculatedAmounts.totalGrams} גרם` : `Total: ${calculatedAmounts.totalGrams}g`}
                </p>
              </div>
              <button onClick={resetCalculator} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                <RefreshCw className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Simplified Results - Mobile First */}
          <div className="p-3 sm:p-5 bg-[#FAF8F5]">
            {/* Instructions */}
            <div className="bg-[#4A5D43]/10 rounded-xl p-3 mb-4 text-center">
              <p className="text-sm text-[#4A5D43] font-medium">
                {isRTL ? '💡 השתמשו בכפות או בכפיות - לפי מה שנוח לכם' : '💡 Use tablespoons OR teaspoons - whichever is convenient'}
              </p>
            </div>

            {/* Ingredients List */}
            <div className="space-y-3">
              {Object.keys(BASE_FORMULA).map(key => {
                const ing = ingredients[key];
                const amounts = calculatedAmounts.amounts[key];
                const isEssential = key === 'essentialOil';
                const hasNote = ing.note;
                
                return (
                  <div key={key} className="bg-white rounded-xl p-3 sm:p-4 border border-[#E8E4DC]">
                    {/* Ingredient Header */}
                    <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row' : 'flex-row'}`}>
                      <span className="text-xl sm:text-2xl">{ing.icon}</span>
                      <div className="flex-1">
                        <p className="font-medium text-[#2C3329] text-sm sm:text-base">{isRTL ? ing.he : ing.en}</p>
                        {hasNote && (
                          <p className="text-xs text-[#4A5D43] bg-[#4A5D43]/10 px-2 py-0.5 rounded-full inline-block mt-1">
                            ✨ {isRTL ? ing.note.he : ing.note.en}
                          </p>
                        )}
                      </div>
                      <span className="text-xs bg-[#4A5D43]/10 text-[#4A5D43] px-2 py-1 rounded-full font-medium">
                        {BASE_FORMULA[key].percent}%
                      </span>
                    </div>
                    
                    {/* Amount Display - Clear and Simple */}
                    <div className={`flex items-center justify-center gap-2 text-center ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                      {/* Grams - Always shown */}
                      <div className="bg-[#4A5D43] text-white rounded-lg px-3 py-2 min-w-[60px]">
                        <p className="text-lg font-bold">{amounts.grams}</p>
                        <p className="text-[10px] opacity-80">{isRTL ? 'גרם' : 'grams'}</p>
                      </div>
                      
                      <span className="text-[#9A9790] text-lg">=</span>
                      
                      {isEssential ? (
                        /* Essential Oil - Just drops */
                        <div className="bg-[#C47F64] text-white rounded-lg px-3 py-2 min-w-[60px]">
                          <p className="text-lg font-bold">{amounts.drops}</p>
                          <p className="text-[10px] opacity-80">{isRTL ? 'טיפות' : 'drops'}</p>
                        </div>
                      ) : (
                        /* Other ingredients - Tablespoons OR Teaspoons */
                        <>
                          <div className="bg-[#C47F64] text-white rounded-lg px-3 py-2 min-w-[60px]">
                            <p className="text-lg font-bold">{amounts.tablespoons}</p>
                            <p className="text-[10px] opacity-80">{isRTL ? 'כפות' : 'tbsp'}</p>
                          </div>
                          
                          <span className="text-[#9A9790] text-xs font-medium">
                            {isRTL ? 'או' : 'OR'}
                          </span>
                          
                          <div className="bg-[#7C9A92] text-white rounded-lg px-3 py-2 min-w-[60px]">
                            <p className="text-lg font-bold">{amounts.teaspoons}</p>
                            <p className="text-[10px] opacity-80">{isRTL ? 'כפיות' : 'tsp'}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-4 bg-white border-t border-[#E8E4DC] flex gap-3">
            <Button
              onClick={handleCopy}
              variant="outline"
              className={`flex-1 rounded-full border-[#4A5D43] text-[#4A5D43] hover:bg-[#4A5D43]/10 ${copied ? 'bg-[#4A5D43]/10' : ''}`}
            >
              {copied ? (
                <>
                  <Check className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'הועתק!' : 'Copied!'}
                </>
              ) : (
                <>
                  <Copy className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'העתקה' : 'Copy'}
                </>
              )}
            </Button>
            <Button
              onClick={handleShare}
              className={`flex-1 btn-primary rounded-full ${shared ? 'bg-[#3A4A35]' : ''}`}
            >
              {shared ? (
                <>
                  <Check className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'שותף!' : 'Shared!'}
                </>
              ) : (
                <>
                  <Share2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'שיתוף' : 'Share'}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Stabilization Tip Callout */}
        <div data-testid="calc-stabilization-tip" className="relative rounded-2xl overflow-hidden border-2 border-[#C47F64]/30 bg-gradient-to-br from-[#FDF8F4] to-[#FAF2EC] mb-3">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C47F64] to-[#D4956F]" />
          <div className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 bg-[#C47F64]/15 rounded-full flex items-center justify-center shrink-0">
                <Info className="h-4 w-4 text-[#C47F64]" />
              </div>
              <h4 className="font-serif text-base text-[#2C3329] font-medium">
                {isRTL ? 'טיפ לייצוב הפורמולה' : 'Formula Stabilization Tip'}
              </h4>
            </div>
            <p className="text-[#6B7266] text-sm leading-relaxed mb-4">
              {isRTL 
                ? "ג׳ל אלוורה מכיל פאזה מימית שעלולה להוביל להיפרדות המוצר. ניתן לוותר עליו (4.5%) ולהוסיף לחמאת השיאה (20% → 24.5%), לייצוב מלא."
                : "Aloe vera gel contains a water phase that may lead to product separation. You can skip it (4.5%) and add to shea butter (20% → 24.5%), for full stability."}
            </p>
            <p className="text-[#6B7266] text-sm leading-relaxed bg-white/60 rounded-lg p-3 border border-[#4A5D43]/10">
              {isRTL 
                ? "לחלופין, ניתן להשתמש בשמן קיק (Castor Oil) כתחליף לג׳ל אלוורה, מתערבב היטב עם שמנים ומספק ייצוב ללא סיכון להיפרדות."
                : "Alternatively, use Castor Oil as an aloe vera substitute, blends well with oils and provides stabilization without separation risk."}
            </p>
          </div>
        </div>

        {/* Tips */}
        <div className="premium-card rounded-2xl p-5 bg-[#4A5D43]/5 border border-[#4A5D43]/20">
          <h4 className="font-serif text-lg text-[#2C3329] mb-3">
            {isRTL ? '💡 טיפים חשובים' : '💡 Important Tips'}
          </h4>
          <ul className="space-y-2 text-sm text-[#6B7266]">
            <li className="flex items-start gap-2">
              <span className="text-[#4A5D43]">•</span>
              {isRTL ? 'כפית = 5 גרם, כף = 15 גרם (בממוצע)' : 'Teaspoon = 5g, Tablespoon = 15g (average)'}
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#4A5D43]">•</span>
              {isRTL ? 'שמנים אתריים: כ-20 טיפות = 1 מ"ל' : 'Essential oils: about 20 drops = 1ml'}
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#4A5D43]">•</span>
              {isRTL ? 'מומלץ להשתמש במשקל מטבח לדיוק מקסימלי' : 'Kitchen scale recommended for accuracy'}
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default QuantityCalculator;
